import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/navbar";
import Footer from "../components/footer";
import Switcher from "../components/switcher";
import FaqContact from "../components/FaqContact";
import Seo from "../components/Seo";
import useStore from "../store/useStore";

const apiUrl = process.env.REACT_APP_STRAPI_URL;

export default function FaqDetail() {
  const { id } = useParams();
  const getById = useStore((s) => s.getPWCPreguntaById);
  const [fetched, setFetched] = useState(null);

  useEffect(() => {
      console.log(id)
      getById(id).then((res) => setFetched(res));
  }, [id, getById]);

  const faq = useMemo(() => {
    if (!fetched) return null;
    const a = fetched || {};
    return {
      id: a.id,
      pregunta: a.Pregunta,
      resumen: a.RespuestaCorta || "",
      contenido: a.Respuesta || "",
      ctaLabel: a.URLInforme ? "Informe completo" : "",
      ctaHref: a.URLInforme || "",
      author: a.pwc_expert?
         {
            name: a.pwc_expert.Nombre || "",
            role:  a.pwc_expert.Cargo || "",
            avatar: a.pwc_expert.Foto?.url ? apiUrl + a.pwc_expert.Foto?.url : "",
            linkedin: a.pwc_expert.Linkedin || "",
          }
        : null,
    };
  }, [fetched]);

  if (!faq) {
    return (
      <div className="min-h-screen bg-sidom-light dark:bg-sidom-blue text-center py-40">
        <Navbar />
        <div className="container">
          <h1 className="text-3xl font-extrabold text-sidom-blue dark:text-sidom-light">
            Pregunta no encontrada
          </h1>
          <p className="mt-4">
            Volver a {" "}
            <Link to="/conocimientopwc" className="text-sidom-orange underline">Conocimiento PwC</Link>
          </p>
        </div>
        <Footer />
        <Switcher />
      </div>
    );
  }

  return (
    <>
      <Seo
        metaTitle={`${faq.pregunta} - Conocimiento PwC`}
        metaDescription={faq.resumen}
      />
      <header>
        <Navbar />
      </header>

      <main className="bg-sidom-light dark:bg-sidom-blue">
        {/* Header minimal (más padding top) */}
        <section className="pt-40 pb-6 bg-sidom-blue" />

        {/* Contenido principal */}
        <section className="container pt-4 pb-12">
          <div className="max-w-5xl mx-auto">
            {/* Botón volver */}
            <div className="mb-4 mt-4">
              <Link
                to="/conocimientopwc"
                className="inline-flex items-center gap-2 text-sidom-orange hover:underline"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Volver
              </Link>
            </div>
            <h1 className="text-2xl md:text-4xl font-extrabold text-sidom-blue dark:text-white mb-6">
              {faq.pregunta}
            </h1>

            <article className="text-sidom-dark dark:text-sidom-light leading-relaxed space-y-6">
              <p className="text-[15px] md:text-base">{faq.contenido}</p>
            </article>

            {/* CTA y Autor en la misma fila */}
            {((faq.ctaLabel && faq.ctaHref) || faq.author) && (
              <div className="mt-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                {/* CTA (izquierda) */}
                <div>
                  {faq.ctaLabel && faq.ctaHref && (
                    <a
                      href={faq.ctaHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sidom-orange font-semibold hover:underline"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10.59 13.41a1.996 1.996 0 0 0 2.82 0l3.59-3.59a2 2 0 1 0-2.83-2.83l-1.17 1.17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M13.41 10.59a1.996 1.996 0 0 0-2.82 0L7 14.17a2 2 0 1 0 2.83 2.83l1.17-1.17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {faq.ctaLabel}
                    </a>
                  )}
                </div>

                {/* Autor (derecha) */}
                {faq.author && (
                  <div className="flex justify-end w-full md:w-auto">
                    <div className="flex items-center gap-4">
                      <img
                        src={faq.author.avatar || "https://ui-avatars.com/api/?background=ef7d54&color=fff&name=" + encodeURIComponent(faq.author.name || "Autor")}
                        alt={faq.author.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div className="text-right">
                        <div className="flex items-center gap-2 justify-end">
                          {faq.author.linkedin && (
                            <a
                              href={faq.author.linkedin}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-sidom-orange hover:text-sidom-orange/80"
                              aria-label="LinkedIn del autor"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M19 0h-14c-2.76 0-5 2.24-5 5v14c0 2.76 2.24 5 5 5h14c2.76 0 5-2.24 5-5v-14c0-2.76-2.24-5-5-5zm-11 19h-3v-10h3v10zm-1.5-11.27c-.96 0-1.73-.79-1.73-1.73 0-.95.78-1.73 1.73-1.73s1.73.78 1.73 1.73c0 .95-.77 1.73-1.73 1.73zm13.5 11.27h-3v-5.6c0-1.34-.03-3.06-1.87-3.06-1.87 0-2.15 1.46-2.15 2.96v5.7h-3v-10h2.88v1.37h.04c.4-.76 1.37-1.56 2.82-1.56 3.01 0 3.57 1.98 3.57 4.55v5.64z"/></svg>
                            </a>
                          )}
                          <span className="text-sidom-blue dark:text-white font-semibold">
                            {faq.author.name}
                          </span>
                        </div>
                        <p className="text-sm text-sidom-dark/70 dark:text-sidom-light/80">{faq.author.role}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

  <FaqContact />
      </main>

      <Footer />
      <Switcher />
    </>
  );
}
